import React, { useRef, useEffect, useMemo } from 'react';
import type { Block as AgentBlock } from '../../../lib/agentloop-stream';
import type { ToolComponent } from '../types';
import { transformBlocks } from '../utils';
import {
  UserMessage,
  AssistantMessage,
  ReasoningBlock,
  ToolBlock,
  ErrorMessage,
} from '../messages';

interface MessageStreamProps {
  blocks: Map<string, AgentBlock>;
  latestError?: {
    id: string;
    content: string;
    errorType?: string;
  };

  // Scroll behavior
  autoScroll?: boolean;
  onScrollStateChange?: (isAutoScrolling: boolean) => void;

  // Callbacks
  onTryAgain?: () => void;

  // Customization
  className?: string;
  messageClassName?: string;
  toolRegistry?: Map<string, ToolComponent>;

  // Props for assistant messages (secret handling)
  applicationId?: string;
  onSendMessage?: (message: string, model: string) => void;
}

export const MessageStream: React.FC<MessageStreamProps> = ({
  blocks,
  latestError,
  autoScroll = true,
  onScrollStateChange,
  onTryAgain,
  className = '',
  messageClassName = '',
  toolRegistry,
  applicationId,
  onSendMessage,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const isAutoScrollingRef = useRef<boolean>(autoScroll);

  // Transform blocks to display messages
  const displayBlocks = useMemo(
    () => transformBlocks(blocks, latestError),
    [blocks, latestError]
  );

  // Auto-scroll when messages change
  useEffect(() => {
    if (isAutoScrollingRef.current && scrollAreaRef.current) {
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [displayBlocks]);

  // Handle scroll to detect if user scrolled up
  const handleScroll = () => {
    if (!scrollAreaRef.current) return;

    const element = scrollAreaRef.current;
    const threshold = 50; // pixels from bottom
    const isAtBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold;

    isAutoScrollingRef.current = isAtBottom;
    onScrollStateChange?.(isAtBottom);
  };

  return (
    <div
      ref={scrollAreaRef}
      onScroll={handleScroll}
      className={`overflow-auto ${className}`}
    >
      <div className="space-y-6 text-xs font-mono" style={{ letterSpacing: '0.05em' }}>
        {displayBlocks.map((block, index) => {
          const isLastError =
            block.type === 'error' && index === displayBlocks.length - 1;

          switch (block.type) {
            case 'user':
              return (
                <UserMessage
                  key={block.id}
                  id={block.id}
                  content={block.content}
                  className={messageClassName}
                />
              );

            case 'assistant':
              return (
                <AssistantMessage
                  key={block.id}
                  id={block.id}
                  content={block.content}
                  applicationId={applicationId}
                  onSendMessage={onSendMessage}
                  className={messageClassName}
                />
              );

            case 'reasoning':
              return (
                <ReasoningBlock
                  key={block.id}
                  id={block.id}
                  content={block.content}
                  isComplete={block.isComplete}
                  className={messageClassName}
                />
              );

            case 'tool':
              return (
                <ToolBlock
                  key={block.id}
                  id={block.id}
                  toolName={block.toolName}
                  toolInput={block.toolInput}
                  toolOutput={block.toolOutput}
                  toolIsError={block.toolIsError}
                  isStreaming={block.isStreaming}
                  toolRegistry={toolRegistry}
                  className={messageClassName}
                />
              );

            case 'error':
              return (
                <ErrorMessage
                  key={block.id}
                  id={block.id}
                  content={block.content}
                  errorType={block.errorType}
                  onTryAgain={onTryAgain}
                  isLastError={isLastError}
                  className={messageClassName}
                />
              );

            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};
